import dayjs from "dayjs";
import React, { useMemo } from "react";
import Calendar, { OnArgs } from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useTranslation } from "react-i18next";
import { NoteDate } from "../../types/noteDate";
import Utils from "../../utils/utils";
import "./CalendarCustom.scss";

interface CalendarCustomProps {
  notes?: NoteDate[];
  onDayClick?: (date: Date) => void;
  disablePastDates?: boolean;
  defaultDate?: Date;
  enabledDates?: Date[];
  disabledDates?: Date[];
  calendarConfig?: TCalendarConfig[];
  enableDay?: boolean;
  handleActiveStartDateChange?: (props: OnArgs) => void;
}
const daysOfWeekName = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const CalendarCustom: React.FC<CalendarCustomProps> = ({
  defaultDate,
  notes,
  onDayClick,
  enabledDates,
  disabledDates,
  disablePastDates,
  calendarConfig,
  enableDay,
  handleActiveStartDateChange,
}) => {
  const handleDayClick = (date: Date) => {
    // setSelectedDate(date);
    onDayClick?.(date);
  };
  const { t } = useTranslation();
  const today = new Date();
  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === "month") {
      const note = notes
        ? notes.find((note) => dayjs(note.date).isSame(date, "day"))
        : null;
      const isToday = dayjs().isSame(date, "day");
      return (
        <>
          <div className="number-date">{date.getDate()}</div>
          <div>
            {isToday ? (
              <div className="today-indicator">{t("Today")}</div>
            ) : note ? (
              <div className="note-indicator">{note.title}</div>
            ) : null}
          </div>
        </>
      );
    }

    return null;
  };

  const calendarConfigModel = useMemo(() => {
    if (calendarConfig?.length) {
      const result = calendarConfig?.reduce(
        (acc, { date, ...rest }) => {
          acc[date] = rest;
          return acc;
        },
        {} as Record<string, Omit<(typeof calendarConfig)[number], "date">>,
      );
      return result;
    }
    return {};
  }, [calendarConfig]);

  const isDisabled = (date: Date) => {
    if (enableDay) return false;

    if (disablePastDates && dayjs(date).isBefore(dayjs(), "day")) {
      return true;
    }
    // Disable specific dates
    if (
      disabledDates &&
      disabledDates.some((disabledDate) =>
        dayjs(disabledDate).isSame(date, "day"),
      )
    ) {
      return true;
    }
    // Enable specific dates
    if (
      enabledDates &&
      !enabledDates.some((enabledDate) =>
        dayjs(enabledDate).isSame(date, "day"),
      )
    ) {
      return true;
    }
    if (calendarConfig) {
      const day = dayjs(date).format("YYYY-MM-DD");
      const hasTimeSlotAvailable =
        Object.keys(calendarConfigModel?.[day]?.availableGuest || {}).length >
        0;
      const isPastDay = dayjs(date).isBefore(today, "day");
      return (
        !calendarConfigModel?.[day]?.timeSlots?.length ||
        !hasTimeSlotAvailable ||
        isPastDay
      );
      // if (calendarConfig?.closedDates?.length) {
      //   if (
      //     calendarConfig?.closedDates.some(({ date: d }) =>
      //       dayjs(date).isSame(dayjs(d), "day"),
      //     )
      //   ) {
      //     return true;
      //   }
      // }
      // if (calendarConfig?.operatingDays) {
      //   const nums = calendarConfig.operatingDays.map((x) => {
      //     return daysOfWeekName.indexOf(x);
      //   });
      //   if (!nums.includes(date.getDay())) {
      //     return true;
      //   }
      // }
    }
  };
  const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
    if (isDisabled(date)) {
      return true;
    }
    return view === "month" && dayjs(date).isBefore(today, "day");
    // return false;
  };

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view === "month") {
      if (isDisabled(date)) {
        return "disabled-day"; // Add a custom CSS class for disabled days
      }
      // Add a custom class for the selected date
      if (defaultDate && dayjs(date).isSame(defaultDate, "day")) {
        return "selected-day"; // Custom selected class
      }
      if (!defaultDate) {
        return ""; // Custom selected class
      }
    }

    return ""; // Default class
  };
  const changeLanguage = (lang: string) => {
    if (lang === "en") {
      return "en-US";
    } else if (lang === "ko") {
      return "ko-KR";
    } else if (lang === "jp") {
      return "ja-JP";
    } else if (lang === "cn") {
      return "zh-CN";
    } else if (lang === "es") {
      return "es-ES";
    } else {
      return "en-US";
    }
  };
  return (
    <div className="calendar-container max-w-max lg:max-w-none">
      <Calendar
        value={defaultDate}
        locale={changeLanguage(Utils.getCurrentLanguage())}
        onClickDay={handleDayClick}
        tileContent={tileContent}
        tileDisabled={tileDisabled}
        tileClassName={tileClassName}
        calendarType="gregory"
        onActiveStartDateChange={handleActiveStartDateChange}
        minDetail="month"
        maxDetail="month"
      />
    </div>
  );
};

export default CalendarCustom;
